import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Car, FileText, Upload, CheckCircle, AlertCircle } from "lucide-react";

const US_STATES = ["Alabama", "Alaska", "Arizona", "California", "Colorado", "Connecticut", "Florida", "Georgia", "Illinois", "New York", "Texas", "Washington"];

export default function DriverApplication() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    driver_license_number: "",
    driver_license_state: "",
    driver_license_expiry: "",
    tlc_license_number: "",
    tlc_license_expiry: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_color: "",
    vehicle_plate: "",
    insurance_company: "",
    insurance_policy_number: "",
    insurance_expiry: "",
    years_of_experience: 0,
    languages: ["Arabic"],
    accept_terms: false
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.accept_terms) {
      toast.error(t("يجب قبول الشروط والأحكام", "You must accept terms and conditions"));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/drivers/apply`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم تقديم طلبك بنجاح!", "Application submitted successfully!"));
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("فشل تقديم الطلب", "Failed to submit application"));
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-4">{t("المعلومات الشخصية", "Personal Information")}</h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">{t("الاسم الكامل", "Full Name")}</Label>
          <Input
            value={formData.full_name}
            onChange={(e) => updateField("full_name", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("البريد الإلكتروني", "Email")}</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => updateField("email", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("رقم الهاتف", "Phone")}</Label>
          <Input
            value={formData.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
            placeholder="+1 (555) 000-0000"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("العنوان", "Address")}</Label>
          <Input
            value={formData.address}
            onChange={(e) => updateField("address", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("المدينة", "City")}</Label>
          <Input
            value={formData.city}
            onChange={(e) => updateField("city", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("الولاية", "State")}</Label>
          <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
            <SelectTrigger className="h-12 bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder={t("اختر الولاية", "Select State")} />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {US_STATES.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("الرمز البريدي", "ZIP Code")}</Label>
          <Input
            value={formData.zip_code}
            onChange={(e) => updateField("zip_code", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-4">{t("رخصة القيادة", "Driver's License")}</h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">{t("رقم الرخصة", "License Number")}</Label>
          <Input
            value={formData.driver_license_number}
            onChange={(e) => updateField("driver_license_number", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("ولاية الرخصة", "License State")}</Label>
          <Select value={formData.driver_license_state} onValueChange={(v) => updateField("driver_license_state", v)}>
            <SelectTrigger className="h-12 bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder={t("اختر الولاية", "Select State")} />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {US_STATES.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("تاريخ انتهاء الرخصة", "License Expiry")}</Label>
          <Input
            type="date"
            value={formData.driver_license_expiry}
            onChange={(e) => updateField("driver_license_expiry", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("رقم رخصة TLC", "TLC License")} ({t("اختياري", "Optional")})</Label>
          <Input
            value={formData.tlc_license_number}
            onChange={(e) => updateField("tlc_license_number", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("سنوات الخبرة", "Years of Experience")}</Label>
          <Input
            type="number"
            value={formData.years_of_experience}
            onChange={(e) => updateField("years_of_experience", parseInt(e.target.value) || 0)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-4">{t("معلومات السيارة", "Vehicle Information")}</h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">{t("الشركة المصنعة", "Make")}</Label>
          <Input
            value={formData.vehicle_make}
            onChange={(e) => updateField("vehicle_make", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
            placeholder="Toyota, Honda, etc."
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("الموديل", "Model")}</Label>
          <Input
            value={formData.vehicle_model}
            onChange={(e) => updateField("vehicle_model", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
            placeholder="Camry, Accord, etc."
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("سنة الصنع", "Year")}</Label>
          <Input
            value={formData.vehicle_year}
            onChange={(e) => updateField("vehicle_year", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
            placeholder="2020"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("اللون", "Color")}</Label>
          <Input
            value={formData.vehicle_color}
            onChange={(e) => updateField("vehicle_color", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("رقم اللوحة", "Plate Number")}</Label>
          <Input
            value={formData.vehicle_plate}
            onChange={(e) => updateField("vehicle_plate", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
      </div>

      <h3 className="text-lg font-bold text-white mt-8 mb-4">{t("التأمين", "Insurance")}</h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">{t("شركة التأمين", "Insurance Company")}</Label>
          <Input
            value={formData.insurance_company}
            onChange={(e) => updateField("insurance_company", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("رقم الوثيقة", "Policy Number")}</Label>
          <Input
            value={formData.insurance_policy_number}
            onChange={(e) => updateField("insurance_policy_number", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("تاريخ انتهاء التأمين", "Insurance Expiry")}</Label>
          <Input
            type="date"
            value={formData.insurance_expiry}
            onChange={(e) => updateField("insurance_expiry", e.target.value)}
            className="h-12 bg-slate-900 border-slate-700 text-white"
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-white mb-4">{t("الشروط والأحكام", "Terms & Conditions")}</h3>
      
      <div className="glass-card rounded-xl p-6 max-h-64 overflow-y-auto">
        <h4 className="font-bold text-white mb-4">{t("اتفاقية السائق", "Driver Agreement")}</h4>
        <div className="text-slate-400 text-sm space-y-3">
          <p>{t("بالتوقيع على هذه الاتفاقية، أوافق على:", "By signing this agreement, I agree to:")}</p>
          <ul className="list-disc list-inside space-y-2">
            <li>{t("الالتزام بجميع قوانين المرور", "Follow all traffic laws")}</li>
            <li>{t("توفير خدمة آمنة ومحترمة للعملاء", "Provide safe and respectful service to customers")}</li>
            <li>{t("الحفاظ على نظافة السيارة", "Keep the vehicle clean")}</li>
            <li>{t("عدم التدخين أثناء القيادة", "No smoking while driving")}</li>
            <li>{t("احترام خصوصية العملاء", "Respect customer privacy")}</li>
            <li>{t("الإبلاغ عن أي حوادث فوراً", "Report any incidents immediately")}</li>
            <li>{t("تحديث معلومات الرخصة والتأمين عند التجديد", "Update license and insurance info upon renewal")}</li>
          </ul>
          <p className="mt-4">{t("أقر بأن جميع المعلومات المقدمة صحيحة ودقيقة.", "I confirm that all information provided is true and accurate.")}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="accept_terms"
          checked={formData.accept_terms}
          onCheckedChange={(checked) => updateField("accept_terms", checked)}
          className="mt-1 border-slate-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
        />
        <Label htmlFor="accept_terms" className="text-slate-300 cursor-pointer">
          {t("أوافق على الشروط والأحكام وأقر بصحة جميع المعلومات المقدمة", "I agree to the terms and conditions and confirm all information is accurate")}
        </Label>
      </div>
    </div>
  );

  return (
    <DashboardLayout title={t("طلب الانضمام كسائق", "Driver Application")}>
      <div className="max-w-3xl mx-auto" data-testid="driver-application">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-yellow-500 text-black' : 'bg-slate-800 text-slate-400'
              }`}>
                {step > s ? <CheckCircle size={20} /> : s}
              </div>
              {s < 4 && <div className={`w-16 md:w-24 h-1 mx-2 ${step > s ? 'bg-yellow-500' : 'bg-slate-800'}`} />}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <div className="glass-card rounded-2xl p-6 md:p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <Button onClick={() => setStep(step - 1)} className="btn-secondary">
                {t("السابق", "Previous")}
              </Button>
            ) : <div />}
            
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} className="btn-primary">
                {t("التالي", "Next")}
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || !formData.accept_terms} className="btn-primary">
                {loading ? t("جاري الإرسال...", "Submitting...") : t("تقديم الطلب", "Submit Application")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
